import dayjs from 'dayjs'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { IRecurringRules } from '@vacationtracker/shared/types/holidays'

import { HolidayRow, HolidayRowsByYear, HolidayUpdatePayload, cellAccess, holidayLength, holidayUpdatePayload, holidaysForYear, isSameHoliday, rowYear, utcToday } from './logic'

/** The holiday form's values: a fixed date is EXACT_DAY, a dynamic one SPECIFIC_DAY. */
export type RecurringType = 'EXACT_DAY' | 'SPECIFIC_DAY'

/** What the Recurrence column reads and offers: not recurring, or one of the form's two rules. */
export type RecurrenceOption = 'NONE' | RecurringType

/** An imported holiday has no rule of its own and reads as a fixed date. */
export function currentRecurrence(row: HolidayRow): RecurrenceOption {
  if (!row.recurring) return 'NONE'
  return row.recurringRules?.specificDaySet ? 'SPECIFIC_DAY' : 'EXACT_DAY'
}

export function recurrenceLockReason(row: HolidayRow): string | null {
  return cellAccess({ field: 'recurring', row, canEdit: true }).lockReason ?? null
}

/** [week of month 1-4, weekday 0 = Sunday … 6, month 1-12], the form's encoding. */
export type DaySet = [number, number, number]

export interface RecurrenceSettings {
  recurringType: RecurringType
  daySet: DaySet
  /** Days in a dynamic span; only read for a multi-day holiday. */
  duration: number
  transfer: boolean
}

export const MIN_SPAN_DAYS = 2
const LAST_WEEK_OF_MONTH = 4

// Stored dates can be instants ("…T00:00:00.000Z"), which dayjs would read a day early behind UTC.
const firstDay = (row: HolidayRow): dayjs.Dayjs => dayjs(holidayDay(row.transferOriginalDate || row.date))

/** March 12, 2026 is the second Thursday of March: [2, 4, 3]. */
export const daySetOf = (date: dayjs.Dayjs): DaySet => [Math.min(Math.ceil(date.date() / 7), LAST_WEEK_OF_MONTH), date.day(), date.month() + 1]

export const spanDays = (span: Pick<HolidayRow, 'date' | 'endDate'>): number =>
  span.endDate ? Math.max(MIN_SPAN_DAYS, dayjs(holidayDay(span.endDate)).diff(holidayDay(span.date), 'day') + 1) : MIN_SPAN_DAYS

/** The years location-year-holiday-updated writes a repeating holiday to: its own up to next year. */
const writtenYears = (row: HolidayRow, today: dayjs.Dayjs): number[] => {
  const years: number[] = []
  for (let year = rowYear(row); year <= Math.max(rowYear(row), today.year() + 1); year++) {
    years.push(year)
  }
  return years
}

/** The longest dynamic span that ends inside its year in every year it is written to; the backend files a spill under the wrong year. */
export function maxDynamicSpan(row: HolidayRow, daySet: DaySet, today: dayjs.Dayjs = utcToday()): number {
  return Math.min(
    ...writtenYears(row, today).map((year) => {
      const start = specificDayIn(year, daySet)
      return start.endOf('year').diff(start, 'day') + 1
    })
  )
}

const validDuration = (duration: number, max: number): number => (Number.isFinite(duration) ? Math.min(max, Math.max(MIN_SPAN_DAYS, Math.round(duration))) : MIN_SPAN_DAYS)

export function recurrenceDefaults(row: HolidayRow): RecurrenceSettings {
  const rules = row.recurringRules
  return {
    recurringType: rules?.specificDaySet ? 'SPECIFIC_DAY' : 'EXACT_DAY',
    daySet: rules?.specificDaySet ?? daySetOf(firstDay(row)),
    duration: rules?.specificDayDuration ?? spanDays(row),
    transfer: Boolean(row.transferIfFallsOnNonWorkingDay),
  }
}

// A stored rule can carry Sunday as 7; dayjs counts it as 0.
const normWeekday = (weekday: number): number => (weekday === 7 ? 0 : weekday)

export function specificDayIn(year: number, [week, weekday, month]: DaySet): dayjs.Dayjs {
  const target = normWeekday(weekday)
  let day = dayjs(`${year}-${String(month).padStart(2, '0')}-01`)
  while (day.day() !== target) {
    day = day.add(1, 'day')
  }
  return day.add(week - 1, 'week')
}

export type RecurrenceSummary =
  | { kind: 'fixed'; date: string; endDate?: string; transfer: boolean }
  | { kind: 'dynamic'; week: number; weekday: number; month: number; transfer: boolean }
  | { kind: 'library' }

export function recurrenceSummary(row: HolidayRow, settings: RecurrenceSettings = recurrenceDefaults(row)): RecurrenceSummary {
  if (settings.recurringType === 'SPECIFIC_DAY') {
    const [week, weekday, month] = settings.daySet
    return { kind: 'dynamic', week, weekday: normWeekday(weekday), month, transfer: settings.transfer }
  }
  const endDate = row.endDate && holidayDay(row.endDate)
  return {
    kind: 'fixed',
    date: firstDay(row).format('YYYY-MM-DD'),
    ...(endDate && endDate !== holidayDay(row.date) ? { endDate } : {}),
    transfer: settings.transfer,
  }
}

/** An imported holiday with no rule of its own follows the library's date, which moves year to year. */
export function savedRecurrenceSummary(row: HolidayRow): RecurrenceSummary {
  if (row.type === 'LIB' && !row.recurringRules) return { kind: 'library' }
  return recurrenceSummary(row)
}

export interface RecurrencePayloadInput {
  row: HolidayRow
  locationId: string
  settings: RecurrenceSettings
  /** Which years the series is written to, for the span cap. */
  today?: dayjs.Dayjs
}

/**
 * The holiday form's update body for a holiday turned repeating, in the row's own year. A dynamic
 * date sends its first day as both ends and the length as `specificDayDuration`, only for a span:
 * the backend reads a missing duration as one day, and the form never sends one for a single day.
 */
export function recurrencePayload({ row, locationId, settings, today = utcToday() }: RecurrencePayloadInput): HolidayUpdatePayload {
  if (settings.recurringType === 'EXACT_DAY') {
    return {
      ...holidayUpdatePayload({ row, locationId, patch: { recurring: true } }),
      transferIfFallsOnNonWorkingDay: settings.transfer,
    }
  }
  const multiDay = holidayLength(row) === 'MULTI_DAY'
  const start = specificDayIn(rowYear(row), settings.daySet).format('YYYY-MM-DD')
  const recurringRules: IRecurringRules = {
    specificDaySet: settings.daySet,
    ...(multiDay ? { specificDayDuration: validDuration(settings.duration, maxDynamicSpan(row, settings.daySet, today)) } : {}),
  }
  return {
    ...holidayUpdatePayload({ row, locationId, patch: { recurring: true, startDate: start, endDate: start } }),
    recurringRules,
    transferIfFallsOnNonWorkingDay: settings.transfer,
  }
}

export interface RecurrenceChangeInput {
  row: HolidayRow
  locationId: string
  /** The rule picked, or null for Not recurring. */
  settings: RecurrenceSettings | null
  today?: dayjs.Dayjs
}

/**
 * The update body for a pick in the Recurrence column, or null when there is nothing to post: the
 * row's own state, or a change location-year-holiday-updated rejects. It rejects every update to a
 * custom repeating holiday, and an imported holiday may only stop repeating.
 */
export function recurrenceChangePayload({ row, locationId, settings, today }: RecurrenceChangeInput): HolidayUpdatePayload | null {
  const imported = row.type === 'LIB'
  if (row.recurring && !imported) return null
  if (!settings) return row.recurring ? holidayUpdatePayload({ row, locationId, patch: { recurring: false } }) : null
  if (row.recurring || imported) return null
  return recurrencePayload({ row, locationId, settings, today })
}

const dayList = (start: dayjs.Dayjs, end: dayjs.Dayjs): dayjs.Dayjs[] => {
  const days: dayjs.Dayjs[] = []
  for (let day = start; !day.isAfter(end, 'day'); day = day.add(1, 'day')) {
    days.push(day)
  }
  return days
}

// createHolidaysCollection's transfer, as the backend runs it: each non-working day moves to the
// next working day of the year after it that the span's other days leave free, or is dropped.
const transferred = (days: dayjs.Dayjs[], year: number, workWeek: number[]): dayjs.Dayjs[] => {
  const isWorking = (day: dayjs.Dayjs) => workWeek.includes(day.day())
  const kept = new Set(days.filter(isWorking).map((day) => day.format('YYYY-MM-DD')))
  const yearEnd = dayjs(`${year}-12-31`)
  const result: dayjs.Dayjs[] = []
  let lastUsed: dayjs.Dayjs | undefined
  days.forEach((day) => {
    if (isWorking(day)) {
      result.push(day)
      return
    }
    let candidate = day.add(1, 'day')
    if (lastUsed && !candidate.isAfter(lastUsed, 'day')) candidate = lastUsed.add(1, 'day')
    for (; !candidate.isAfter(yearEnd, 'day'); candidate = candidate.add(1, 'day')) {
      if (isWorking(candidate) && !kept.has(candidate.format('YYYY-MM-DD'))) {
        result.push(candidate)
        lastUsed = candidate
        return
      }
    }
  })
  return result
}

/** The days an update body puts the holiday on in `year`, after any transfer. */
export function occurrenceDays(payload: HolidayUpdatePayload, year: number, workWeek?: number[]): string[] {
  const rules = payload.recurringRules
  let start: dayjs.Dayjs
  let end: dayjs.Dayjs
  if (payload.recurring && rules?.specificDaySet) {
    start = specificDayIn(year, rules.specificDaySet)
    end = start.add((rules.specificDayDuration || 1) - 1, 'day')
  } else {
    start = dayjs(holidayDay(payload.startDate)).year(year)
    end = dayjs(holidayDay(payload.endDate)).year(year)
  }
  let days = dayList(start, end)
  // Without the location's week the transfer is unknown; checking the untransferred days is the stricter guess.
  if (payload.transferIfFallsOnNonWorkingDay && workWeek?.length) {
    days = transferred(days, year, workWeek)
  }
  return days.map((day) => day.format('YYYY-MM-DD'))
}

export interface RecurrenceClashInput {
  /** The row as it stands before the change. */
  row: HolidayRow
  payload: HolidayUpdatePayload
  byYear: HolidayRowsByYear[]
  workWeek?: number[]
  today?: dayjs.Dayjs
}

export interface RecurrenceClash {
  name: string
  year: number
}

/**
 * The first holiday the change would land on, in any year the backend writes: a repeating holiday
 * its own year up to next year, otherwise its own year. In the row's year its group and series
 * never count; in later years the backend first removes every row with its id.
 */
export function recurrenceClash({ row, payload, byYear, workWeek, today = utcToday() }: RecurrenceClashInput): RecurrenceClash | undefined {
  const years = payload.recurring ? writtenYears(row, today) : [rowYear(row)]
  for (const year of years) {
    const days = occurrenceDays(payload, year, workWeek)
    const own = year === rowYear(row)
    const clash = holidaysForYear(byYear, year).find(
      (other) =>
        !(own ? isSameHoliday(row, other) : Boolean(row.id) && other.id === row.id) &&
        days.some((day) => holidayDay(other.date) <= day && day <= holidayDay(other.endDate ?? other.date))
    )
    if (clash) return { name: clash.name, year }
  }
  return undefined
}
